import React, { useState, useMemo } from 'react';
import { ArrowRight, CheckSquare, Square, Users, GraduationCap, AlertTriangle,
  CheckCircle, TrendingUp, CalendarCheck, IndianRupee, Plus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { api } from '../../utils/api';
import { generateId } from '../../utils/helpers';

function getClassNumber(name = '') {
  const m = name.match(/\d+/);
  return m ? parseInt(m[0]) : 0;
}

function nextYearStr(year = '') {
  const m = year.match(/(\d{4})-(\d{4})/);
  if (!m) return '';
  return `${parseInt(m[1]) + 1}-${parseInt(m[2]) + 1}`;
}

function PctBar({ pct, colorClass }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
        <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`text-xs font-semibold ${pct >= 75 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{pct}%</span>
    </div>
  );
}

export default function Promotion() {
  const { students, classes, setStudents, attendance, marks, exams,
    feeStructures, feePayments, setFeeStructures, saveFeeStructure } = useApp();

  const currentYear = useMemo(() => {
    const years = [...new Set(classes.map(c => c.academicYear).filter(Boolean))];
    return years.sort().reverse()[0] || '2025-2026';
  }, [classes]);

  const newYear = nextYearStr(currentYear);

  const sortedClasses = useMemo(() =>
    [...classes].sort((a, b) => getClassNumber(a.name) - getClassNumber(b.name)),
  [classes]);

  function getNextClass(cls) {
    const nextNum = getClassNumber(cls.name) + 1;
    return sortedClasses.find(c =>
      getClassNumber(c.name) === nextNum && c.sectionId === cls.sectionId
    ) || sortedClasses.find(c => getClassNumber(c.name) === nextNum) || null;
  }

  // Helpers for student stats
  const getAttendance = (studentId) => {
    const recs = attendance.filter(a => a.entityType === 'student' && a.entityId === studentId);
    const total = recs.length;
    const present = recs.filter(a => a.status === 'present' || a.status === 'late').length;
    return { total, present, pct: total > 0 ? Math.round((present / total) * 100) : null };
  };

  const getExamPct = (studentId) => {
    const m = marks.filter(x => x.studentId === studentId);
    if (!m.length) return null;
    const obt = m.reduce((s, x) => s + (x.marksObtained || 0), 0);
    const max = m.reduce((s, x) => s + (x.maxMarks || 0), 0);
    return max > 0 ? Math.round((obt / max) * 100) : null;
  };

  const getFeeBalance = (student) => {
    // fee_structures.class_id stores the class NAME (e.g. "Class 8"), not a
    // class row id — match on name, falling back to raw id for safety.
    const className = classes.find(c => c.id === student.classId)?.name;
    const structure = [...feeStructures]
      .filter(f => (className && f.classId === className) || f.classId === student.classId)
      .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0];
    if (!structure) return 0;
    const totalDue = (Number(structure.totalAmount) || 0) + (Number(student.previousYearBalance) || 0);
    const paid = feePayments.filter(p => p.studentId === student.id)
      .reduce((sum, p) => sum + (Number(p.amount) || 0) + (Number(p.discount) || 0), 0);
    return Math.max(0, totalDue - paid);
  };

  // Group active students by classId
  const grouped = useMemo(() => {
    const map = {};
    for (const cls of sortedClasses) {
      const studs = students.filter(s => s.classId === cls.id && s.status === 'active');
      if (studs.length > 0) map[cls.id] = { cls, studs };
    }
    return map;
  }, [students, sortedClasses]);

  // Track which classes have been promoted this session
  const [promotedClassIds, setPromotedClassIds] = useState(new Set());

  // checked = set of studentIds selected for promotion
  const allActiveIds = useMemo(() =>
    Object.values(grouped).flatMap(g => g.studs.map(s => s.id)),
  [grouped]);

  const [checked, setChecked] = useState(() => new Set(allActiveIds));
  const [confirmClassId, setConfirmClassId] = useState(null); // which class to confirm
  const [newFeeStructures, setNewFeeStructures] = useState([]); // classes needing new fee structure

  const toggleStudent = (id) =>
    setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleClass = (studs) => {
    const ids = studs.map(s => s.id);
    const allOn = ids.every(id => checked.has(id));
    setChecked(prev => {
      const n = new Set(prev);
      ids.forEach(id => allOn ? n.delete(id) : n.add(id));
      return n;
    });
  };

  async function promoteClass(classId) {
    const { cls, studs } = grouped[classId];
    const nextCls = getNextClass(cls);
    const toPromote = studs.filter(s => checked.has(s.id));

    // Build updated student objects
    const updatedStudents = toPromote.map(s => {
      const feeBalance = getFeeBalance(s);
      if (!nextCls) return { ...s, status: 'graduated', previousYearBalance: feeBalance || 0 };
      return { ...s, classId: nextCls.id, previousYearBalance: feeBalance || 0 };
    });

    // Persist each student update to the database
    try {
      await Promise.all(updatedStudents.map(s => api.update('students', s.id, s)));
    } catch (e) {
      alert(`Promotion failed: ${e.message}`);
      setConfirmClassId(null);
      return;
    }

    // Update local React state after successful API calls
    setStudents(prev => prev.map(s => {
      const updated = updatedStudents.find(u => u.id === s.id);
      return updated || s;
    }));

    setPromotedClassIds(prev => new Set([...prev, classId]));

    // Check if new class needs a fee structure for the new year
    if (nextCls) {
      const exists = feeStructures.find(f => f.classId === nextCls.name && f.academicYear === newYear);
      if (!exists) {
        const template = [...feeStructures]
          .filter(f => f.classId === cls.name)
          .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0];
        setNewFeeStructures(prev => {
          if (prev.find(x => x.classId === nextCls.id)) return prev;
          return [...prev, { classId: nextCls.id, className: nextCls.name, template }];
        });
      }
    }

    setConfirmClassId(null);
  }

  async function createFeeStructure(item) {
    const template = item.template;
    // fee_structures.class_id stores the class NAME, not the row id — see FeeStructure.js
    const newFs = template
      ? { ...template, id: generateId('FEE'), classId: item.className, academicYear: newYear }
      : { id: generateId('FEE'), name: `Fee - ${item.className}`, classId: item.className,
          academicYear: newYear, dueDate: '', frequency: 'annually',
          components: [{ name: 'Tuition Fee', amount: 0 }], totalAmount: 0 };
    // Persist to database — saveFeeStructure also updates local state on success
    const ok = await saveFeeStructure(newFs);
    if (ok) {
      setNewFeeStructures(prev => prev.filter(x => x.classId !== item.classId));
    }
  }

  const totalStudents = allActiveIds.length;
  const pendingClasses = Object.keys(grouped).filter(id => !promotedClassIds.has(id));
  const allDone = pendingClasses.length === 0 && totalStudents > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-5">

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h1 className="text-xl font-bold text-gray-800 mb-1">Student Promotion</h1>
        <p className="text-gray-500 text-sm">Promote students to the next class. Review attendance and exam performance before confirming.</p>
        <div className="mt-4 flex items-center gap-3">
          <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-semibold text-sm border border-blue-200">{currentYear}</span>
          <ArrowRight size={20} className="text-gray-400" />
          <span className="px-4 py-2 bg-green-50 text-green-700 rounded-lg font-semibold text-sm border border-green-200">{newYear}</span>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-800">{totalStudents}</p>
          <p className="text-gray-500 text-sm mt-0.5">Total Students</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{promotedClassIds.size}</p>
          <p className="text-gray-500 text-sm mt-0.5">Classes Promoted</p>
        </div>
        <div className="bg-white rounded-xl border border-orange-200 p-4 text-center">
          <p className="text-2xl font-bold text-orange-500">{pendingClasses.length}</p>
          <p className="text-gray-500 text-sm mt-0.5">Classes Pending</p>
        </div>
      </div>

      {/* All done banner */}
      {allDone && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center gap-3">
          <CheckCircle size={28} className="text-green-500 flex-shrink-0" />
          <div>
            <p className="font-semibold text-green-800">All classes have been promoted to {newYear}!</p>
            <p className="text-green-600 text-sm">Fee carry-forwards have been applied to each student's record.</p>
          </div>
        </div>
      )}

      {/* New fee structure prompts */}
      {newFeeStructures.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="font-semibold text-amber-800 flex items-center gap-2">
            <IndianRupee size={16} /> New Year Fee Structures Required
          </p>
          {newFeeStructures.map(item => (
            <div key={item.classId} className="flex items-center justify-between bg-white rounded-lg border border-amber-100 px-4 py-2.5">
              <div>
                <p className="text-sm font-medium text-gray-800">{item.className} — {newYear}</p>
                {item.template
                  ? <p className="text-xs text-gray-500">Will clone fee structure from {currentYear} (₹{item.template.totalAmount?.toLocaleString('en-IN')})</p>
                  : <p className="text-xs text-gray-500">No existing structure found — will create blank</p>}
              </div>
              <button onClick={() => createFeeStructure(item)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors">
                <Plus size={13} /> Create
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Class cards */}
      {Object.keys(grouped).length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
          No active students found.
        </div>
      )}

      {Object.values(grouped).map(({ cls, studs }) => {
        const nextCls = getNextClass(cls);
        const isPromoted = promotedClassIds.has(cls.id);
        const allOn = studs.every(s => checked.has(s.id));
        const someOn = studs.some(s => checked.has(s.id));
        const selectedCount = studs.filter(s => checked.has(s.id)).length;

        return (
          <div key={cls.id} className={`bg-white rounded-xl border overflow-hidden ${isPromoted ? 'border-green-200 opacity-75' : 'border-gray-200'}`}>
            {/* Class header */}
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isPromoted ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-3">
                {!isPromoted && (
                  <button onClick={() => toggleClass(studs)} className="text-gray-500 hover:text-purple-600 transition-colors">
                    {allOn ? <CheckSquare size={18} className="text-purple-600" />
                      : someOn ? <CheckSquare size={18} className="text-purple-300" />
                      : <Square size={18} />}
                  </button>
                )}
                {isPromoted && <CheckCircle size={18} className="text-green-500 flex-shrink-0" />}
                <span className="font-semibold text-gray-800">{cls.name}{cls.sectionId ? ` - Section ${cls.sectionId}` : ''}</span>
                <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">{studs.length} students</span>
                {isPromoted && (
                  <span className="text-xs bg-green-100 text-green-700 font-semibold rounded-full px-2.5 py-0.5 border border-green-200">Promoted</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  {nextCls ? (
                    <>
                      <span className="text-gray-400 text-xs">→</span>
                      <span className="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-md border border-green-200 text-xs">
                        {nextCls.name}{nextCls.sectionId ? ` - ${nextCls.sectionId}` : ''}
                      </span>
                    </>
                  ) : (
                    <span className="flex items-center gap-1 text-purple-600 font-semibold bg-purple-50 px-2 py-0.5 rounded-md border border-purple-200 text-xs">
                      <GraduationCap size={13} /> Graduates
                    </span>
                  )}
                </div>
                {!isPromoted && (
                  <button
                    onClick={() => setConfirmClassId(cls.id)}
                    disabled={selectedCount === 0}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1">
                    <Users size={13} /> Promote {selectedCount}
                  </button>
                )}
              </div>
            </div>

            {/* Column headers */}
            <div className="grid grid-cols-12 gap-2 px-5 py-1.5 bg-gray-50 border-b border-gray-100 text-xs text-gray-400 font-medium">
              <div className="col-span-1"></div>
              <div className="col-span-1 text-center">#</div>
              <div className="col-span-3">Student</div>
              <div className="col-span-2">Roll No</div>
              <div className="col-span-2 flex items-center gap-1"><CalendarCheck size={11} /> Attendance</div>
              <div className="col-span-2 flex items-center gap-1"><TrendingUp size={11} /> Exam</div>
              <div className="col-span-1 flex items-center gap-1"><IndianRupee size={11} /> Due</div>
            </div>

            {/* Student rows */}
            <div className="divide-y divide-gray-100">
              {studs.map((s, i) => {
                const att = getAttendance(s.id);
                const examPct = getExamPct(s.id);
                const feeBal = getFeeBalance(s);
                const isChecked = checked.has(s.id);
                const attColor = att.pct === null ? '' : att.pct >= 75 ? 'bg-green-500' : att.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
                const examColor = examPct === null ? '' : examPct >= 75 ? 'bg-green-500' : examPct >= 50 ? 'bg-yellow-500' : 'bg-red-500';

                return (
                  <div key={s.id} className={`grid grid-cols-12 gap-2 items-center px-5 py-2 hover:bg-gray-50 transition-colors text-sm ${!isChecked && !isPromoted ? 'opacity-50' : ''}`}>
                    <div className="col-span-1">
                      {!isPromoted && (
                        <button onClick={() => toggleStudent(s.id)} className="text-gray-400 hover:text-purple-600 transition-colors">
                          {isChecked ? <CheckSquare size={15} className="text-purple-600" /> : <Square size={15} />}
                        </button>
                      )}
                    </div>
                    <div className="col-span-1 text-center text-gray-400 text-xs">{i + 1}</div>
                    <div className="col-span-3 font-medium text-gray-800 truncate">{s.firstName} {s.lastName}</div>
                    <div className="col-span-2 text-gray-500 text-xs">{s.rollNumber || '—'}</div>
                    <div className="col-span-2">
                      {att.pct !== null
                        ? <PctBar pct={att.pct} colorClass={attColor} />
                        : <span className="text-xs text-gray-300">—</span>}
                      {att.total > 0 && <span className="text-gray-400 text-xs ml-0.5">({att.present}/{att.total}d)</span>}
                    </div>
                    <div className="col-span-2">
                      {examPct !== null
                        ? <PctBar pct={examPct} colorClass={examColor} />
                        : <span className="text-xs text-gray-300">—</span>}
                    </div>
                    <div className="col-span-1 text-xs">
                      {feeBal > 0
                        ? <span className="text-red-600 font-semibold">₹{feeBal.toLocaleString('en-IN')}</span>
                        : <span className="text-green-600 font-medium">Clear</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Fee carry-forward note */}
            {!isPromoted && studs.some(s => getFeeBalance(s) > 0) && (
              <div className="px-5 py-2 bg-red-50 border-t border-red-100 text-xs text-red-600 flex items-center gap-1.5">
                <AlertTriangle size={12} /> Pending fee amounts will be carried forward as "Previous Year Balance" after promotion.
              </div>
            )}
          </div>
        );
      })}

      {/* Confirm modal for individual class */}
      {confirmClassId && grouped[confirmClassId] && (() => {
        const { cls, studs } = grouped[confirmClassId];
        const nextCls = getNextClass(cls);
        const toPromote = studs.filter(s => checked.has(s.id));
        const holdBack = studs.filter(s => !checked.has(s.id));
        const feeCarry = toPromote.reduce((sum, s) => sum + getFeeBalance(s), 0);
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 mb-1">Confirm Class Promotion</h3>
              <p className="text-gray-500 text-sm mb-4">
                Promoting <strong>{cls.name}{cls.sectionId ? ` - ${cls.sectionId}` : ''}</strong> → <strong>{nextCls ? nextCls.name : 'Graduated'}</strong>
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm bg-green-50 rounded-lg px-3 py-2">
                  <span className="text-gray-600">Students to promote</span>
                  <span className="font-semibold text-green-700">{toPromote.length}</span>
                </div>
                {holdBack.length > 0 && (
                  <div className="flex justify-between text-sm bg-orange-50 rounded-lg px-3 py-2">
                    <span className="text-gray-600">Held back</span>
                    <span className="font-semibold text-orange-600">{holdBack.length}</span>
                  </div>
                )}
                {feeCarry > 0 && (
                  <div className="flex justify-between text-sm bg-red-50 rounded-lg px-3 py-2">
                    <span className="text-gray-600">Fee carry-forward</span>
                    <span className="font-semibold text-red-600">₹{feeCarry.toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmClassId(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium">Cancel</button>
                <button onClick={() => promoteClass(confirmClassId)} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700">Confirm</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
